import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ArrowRight, Home, Navigation, Building2, Zap } from "lucide-react";
import { useContactInfo } from "@/hooks/useContactInfo";
import Logo from "@/components/Logo";
import { SERVICE_AREA } from "@/data/serviceArea";
import { CITY_LANDING_PAGES } from "@/data/cityLandingPages";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { contactInfo } = useContactInfo();

  // Direct links to service detail pages for better SEO crawlability
  const serviceLinks = [
    { label: "Elektroinstalace", href: "/sluzby/elektroinstalacni-prace" },
    { label: "Elektromontáže", href: "/sluzby/elektromontazni-prace" },
    { label: "Demontáže", href: "/sluzby/demontazni-prace" },
    { label: "Servis a opravy", href: "/sluzby/servis-a-opravy" },
    { label: "Revize", href: "/sluzby/revize-elektroinstalaci" },
    { label: "Pohotovost 24/7", href: "/pohotovost" },
  ];

  const companyLinks = [
    { label: "Domů", href: "/", icon: Home },
    { label: "O nás", href: "/o-nas" },
    { label: "Realizace", href: "/realizace" },
    { label: "Spolupráce", href: "/spoluprace" },
    { label: "Kontakt", href: "/kontakt" },
  ];

  // City landing pages for internal linking
  const cityLinks = Object.values(CITY_LANDING_PAGES).map(city => ({
    label: city.name,
    href: city.urlPath,
  }));

  const phoneHref = contactInfo.phone ? `tel:${contactInfo.phone.replace(/\s/g, '')}` : 'tel:+420732658712';
  const emailHref = contactInfo.email ? `mailto:${contactInfo.email}` : 'mailto:josefkotrba@post.cz';

  return (
    <footer 
      className="py-12 md:py-16 bg-slate-900 text-white" 
      role="contentinfo" 
      aria-label="Patička webu"
      itemScope 
      itemType="https://schema.org/WPFooter"
    >
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-6 gap-8 md:gap-10 mb-10">
          {/* Logo & Description */}
          <div className="col-span-2 md:col-span-1 lg:col-span-1">
            <Link to="/" className="inline-block mb-4" aria-label="ESKO Servis - domovská stránka">
              <Logo variant="dark" size="sm" />
            </Link>
            <p className="text-white/60 text-sm mb-5 max-w-xs">
              Profesionální elektroinstalační služby pro Teplice a {SERVICE_AREA.region} do {SERVICE_AREA.radius} km. 
              Kvalita, spolehlivost a férové ceny.
            </p>
            <a 
              href={phoneHref}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-slate-900 rounded-lg font-semibold text-sm hover:bg-primary/90 transition-colors shadow-lg shadow-primary/20"
              aria-label={`Zavolat na číslo ${contactInfo.phone || '+420 732 658 712'}`}
            >
              <Phone className="h-4 w-4" aria-hidden="true" />
              {contactInfo.phone || '+420 732 658 712'}
            </a>
          </div>

          {/* Services Links - Direct to subpages */}
          <nav aria-label="Navigace služeb">
            <h3 className="font-bold text-base mb-4 text-white">Služby</h3>
            <ul className="space-y-2.5">
              {serviceLinks.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="flex items-center gap-2 text-sm text-white/60 hover:text-primary transition-colors group"
                  >
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform flex-shrink-0" aria-hidden="true" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Company Links */}
          <nav aria-label="Navigace firmy">
            <h3 className="font-bold text-base mb-4 text-white">Firma</h3>
            <ul className="space-y-2.5">
              {companyLinks.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="flex items-center gap-2 text-sm text-white/60 hover:text-primary transition-colors group"
                  >
                    {link.icon ? (
                      <link.icon className="h-3 w-3 text-primary flex-shrink-0" aria-hidden="true" />
                    ) : (
                      <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform flex-shrink-0" aria-hidden="true" />
                    )}
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* City Landing Pages - NEW SECTION for SEO */}
          <nav aria-label="Elektrikář v dalších městech">
            <h3 className="font-bold text-base mb-4 text-white flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary" aria-hidden="true" />
              Elektrikář
            </h3>
            <ul className="space-y-2.5">
              {cityLinks.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href} 
                    className="flex items-center gap-2 text-sm text-white/60 hover:text-primary transition-colors group"
                    aria-label={`Elektrikář ${link.label}`}
                  >
                    <ArrowRight className="h-3 w-3 group-hover:translate-x-1 transition-transform flex-shrink-0" aria-hidden="true" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Service Area */}
          <nav aria-label="Oblast působnosti">
            <h3 className="font-bold text-base mb-4 text-white flex items-center gap-2">
              <Navigation className="h-4 w-4 text-primary" aria-hidden="true" />
              Oblast
            </h3>
            
            {/* Primární oblast - Ústecký kraj */}
            <div className="mb-4">
              <p className="text-xs text-white/40 uppercase tracking-wide mb-2 flex items-center gap-1">
                ⚡ {SERVICE_AREA.region}
              </p>
              <ul className="space-y-1.5">
                <li className="text-sm text-primary font-semibold">{SERVICE_AREA.center.name}</li>
                {SERVICE_AREA.mainCities.slice(0, 3).map((city) => (
                  <li key={city.name} className="text-sm text-white/60">
                    {city.name}
                  </li>
                ))}
                <li className="text-xs text-white/40">
                  + další do {SERVICE_AREA.radius} km
                </li>
              </ul>
            </div>
            
            {/* Sekundární oblast - Praha */}
            <div className="pt-3 border-t border-white/10">
              <p className="text-xs text-blue-400/80 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Building2 className="h-3 w-3" aria-hidden="true" />
                {SERVICE_AREA.secondaryRegion.name}
              </p>
              <ul className="space-y-1.5">
                {SERVICE_AREA.secondaryRegion.cities.slice(0, 2).map((city) => (
                  <li key={city.name} className="text-sm text-white/50">
                    {city.name}
                  </li>
                ))}
              </ul>
            </div>
          </nav>

          {/* Contact Info - Extended with full address */}
          <div>
            <h3 className="font-bold text-base mb-4 text-white">Kontakt</h3>
            <address 
              className="not-italic"
              itemScope 
              itemType="https://schema.org/LocalBusiness"
            >
              <meta itemProp="name" content="ESKO Servis" />
              <ul className="space-y-3">
                <li>
                  <a 
                    href={phoneHref}
                    className="flex items-start gap-3 text-sm text-white/60 hover:text-primary transition-colors"
                    aria-label={`Telefonní číslo: ${contactInfo.phone || '+420 732 658 712'}`}
                    itemProp="telephone"
                  >
                    <Phone className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{contactInfo.phone || '+420 732 658 712'}</span>
                  </a>
                </li>
                <li>
                  <a 
                    href={emailHref}
                    className="flex items-start gap-3 text-sm text-white/60 hover:text-primary transition-colors"
                    aria-label={`E-mail: ${contactInfo.email || 'josefkotrba@post.cz'}`}
                    itemProp="email"
                  >
                    <Mail className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <span>{contactInfo.email || 'josefkotrba@post.cz'}</span>
                  </a>
                </li>
                <li 
                  className="flex items-start gap-3 text-sm text-white/60"
                  itemProp="address" 
                  itemScope 
                  itemType="https://schema.org/PostalAddress"
                >
                  <MapPin className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
                  <div>
                    <span itemProp="streetAddress">Českobratrská 1556/37</span><br />
                    <span itemProp="postalCode">415 01</span>{" "}
                    <span itemProp="addressLocality">Teplice</span>
                    <meta itemProp="addressCountry" content="CZ" />
                    <meta itemProp="addressRegion" content={SERVICE_AREA.region} />
                  </div>
                </li>
              </ul>
            </address>
          </div>
        </div>

        {/* Bottom bar with business details */}
        <div className="pt-6 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
            <p>
              © <time dateTime={String(currentYear)}>{currentYear}</time> ESKO Servis. Všechna práva vyhrazena.
            </p>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-1">
              <span>IČO: {contactInfo.ico || '09930761'}</span>
              {contactInfo.dic && <span>DIČ: {contactInfo.dic}</span>}
              <span className="hidden md:inline">|</span>
              <span>Registrovaná firma</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
